import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getSyncPoints } from "../api/syncPoints.api";
import type { SyncPointItem, SyncPointsStatus } from "../types/syncPoints.types";

const pageSize = 20;
export function useSyncPoints() {
  const [items, setItems] = useState<SyncPointItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<SyncPointsStatus>("all");
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();
  const refetch = useCallback(async () => {
    setError(null);
    try {
      const result = await run((signal) =>
        getSyncPoints({ index: page - 1, size: pageSize, name, status }, signal),
      );
      if (result) {
        setItems(result.items);
        setTotalCount(result.totalCount);
      }
    } catch (requestError: unknown) {
      setItems([]);
      setTotalCount(0);
      setError(getApiErrorMessage(requestError, "Failed to load sync points"));
    }
  }, [name, page, run, status]);
  useEffect(() => {
    const timer = window.setTimeout(() => void refetch(), 300);
    return () => window.clearTimeout(timer);
  }, [refetch]);
  const updateName = useCallback((value: string) => {
    setName(value);
    setPage(1);
  }, []);
  const updateStatus = useCallback((value: SyncPointsStatus) => {
    setStatus(value);
    setPage(1);
  }, []);
  return {
    items,
    totalCount,
    page,
    name,
    status,
    error,
    loading,
    refetch,
    setPage,
    updateName,
    updateStatus,
    pageSize,
  };
}
